import React, { useContext, useEffect, useRef } from 'react';

import { AddIcon, ArrowRightIcon, CurrencyIcon, FullscreenIcon } from '$partials/icon';
import { AmenitiesModal, BookViewingModal, FloorplanModal, GalleryModal, MapModal, ShareModal, StampDutyModal } from '$partials/modals';
import { Button, Tag, Tooltip } from '$partials/ui';
import { findModuleGalleryImages } from '$utilities/data';;
import { formatNumber, formatPrice, formatRoomRange } from '$utilities/format';
import { Image } from '$partials/media';
import { ModalContext } from '$contexts/modal.jsx';
import { Stream } from '$partials/modules';
import { useCursorContext } from '$contexts/cursor.jsx';

const MAP_KEY = 'AIzaSyBQfzQhBmFhT9_YJzsyWWZX4qdFx6UG0WM';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').PropertyPage}
 */
const PropertyPage = (props) => {
    const isProduction = props.domain === 'https://www.russellsimpson.co.uk';
    const page = props.data?.page || {};
    const fxRates = props.data.exchangeRates;
    const rate = fxRates?.USD / fxRates?.GBP;
    const expandButtonRef = useRef(null);
    const { dispatch: cursorDispatch } = useCursorContext();
    const { handleModal } = useContext(ModalContext);

    useEffect(() => {
        const expand = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: expandButtonRef,
                cursorType: 'expand'
            },
        });

        return () => [expand].map(cleanup => cleanup());
    }, []);

    let galleryImages = [];
    if (page.heroImage) {
        galleryImages.push({ image: page.heroImage, video: page.heroVideo });
    }
    if (page.modules) {
        galleryImages = galleryImages.concat(findModuleGalleryImages(page.modules));
    }

    const floorplanImages = [...page?.floorplanImages?.map(f => f.image), page.floorplanImage].filter(Boolean);

    return (
        <main className='container'>
            {/* Hero image */}
            <div
                className='width-100vw trunk-full-width mtn-6 sm-mtn-5 md-mtn-9 cursor-none'
                onClick={() => { handleModal(<GalleryModal images={galleryImages} />); }}
                ref={expandButtonRef}>
                <figure>
                    <div className='md-hide'>
                        {page.heroVideo ? (
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                src={page.heroVideo.videoUrl}/>
                        ) : (
                            <Image {...page.heroImage} />
                        )}
                    </div>
                    <div className='md-show'>
                        {page.heroVideo ? (
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                src={page.heroVideo.videoUrl}
                                width={'100%'}/>
                        ) : (
                            <Image
                                {...page?.desktopHeroImage}
                                height={810}
                                width={1440} />
                        )}
                    </div>
                </figure>
                <div className='absolute md-hide bottom-0 right right-0 mb5 mr5'>
                    <Button
                        as = 'button'
                        className = 'bg-white black p3'
                        look = 'button'
                        renderIcon = {<FullscreenIcon />}/>
                </div>
            </div>

            <section className='width-100vw trunk-full-width property-info-cluster overflow-hidden'>
                <div className='container pb7 pt5 md-pt7'>
                    <div className='col-12 flex justify-between mb5 md-mb6'>
                        <div className='col-12 sm-col-6 md-col-4'>
                            <span className='hide sm-show'>
                                <p className='type-style-5'>{page?.propertyName}</p>
                                <p className='type-style-5'>{page?.area}{page?.area && page?.outwardCode ? ', ' : ''}{page?.outwardCode}</p>
                            </span>
                            <span className='sm-hide'>
                                <p className='type-style-5'>
                                    {page?.propertyName}{page?.propertyName && page?.area ? ', ' : ''}
                                    {page?.area}{page?.area && page?.outwardCode ? ', ' : ''}{page?.outwardCode}
                                </p>
                            </span>
                        </div>
                        <div className='hide sm-show'>
                            <div className='flex flex-wrap justify-end'>
                                <button
                                    aria-label='Stamp Duty'
                                    className='form-field-reset link-reset type-style-5 ml9 cursor-pointer'
                                    onClick={() => {
                                        handleModal(<StampDutyModal {...page} />);
                                    }}
                                    type='button'>Stamp Duty</button>
                                {(page?.amenities?.length > 0) &&
                                    <button
                                        aria-label='Amenties'
                                        className='form-field-reset link-reset type-style-5 ml9 cursor-pointer'
                                        onClick={() => {
                                            handleModal(<AmenitiesModal
                                                amenities={page?.amenities}
                                                description={page?.amenitiesDescription} />);
                                        }}
                                        type='button'>
                                        Amenities
                                    </button>
                                }
                                <a
                                    className='link-reset type-style-5 ml9'
                                    href={page.customPdf?.url ? page.customPdf.url : isProduction ? `https://www.russellsimpson.co.uk/generate-brochure/${props.data.uri}/${page.revisionId}` : `/brochure/${props.data?.uri}`}
                                    rel='noreferrer'
                                    target='_blank'>
                                    Brochure
                                </a>
                                <button
                                    aria-label='Share'
                                    className='form-field-reset link-reset type-style-5 ml9 cursor-pointer'
                                    onClick={() => {
                                        handleModal(<ShareModal />);
                                    }}
                                    type='button'>
                                    Share
                                </button>
                            </div>
                        </div>
                    </div>

                    <hr className='black'/>

                    <div className='col-12 flex flex-wrap justify-between mt5 md-mt7'>
                        <div className='col-12 sm-col-6 md-col-7 mb5 sm-mb7 md-mb10 sm-pr8 md-pr0'>
                            <h1 className='type-style-1'>{page?.heading}</h1>
                        </div>
                        <div className='col-12 sm-col-6 md-col-3 flex flex-wrap'>
                            {page.publicSellingStatus !== 'Sold' && page?.qualifier === 'offersInExcess' && (
                                <p className='type-style-5'>
                                    Offers IEO
                                </p>
                            )}
                            {page.publicSellingStatus !== 'Sold' && page?.qualifier === 'offersOver' && (
                                <p className='type-style-5'>
                                    Offers Over
                                </p>
                            )}
                            {page.publicSellingStatus !== 'Sold' && page?.qualifier === 'guidePrice' && (
                                <p className='type-style-5'>
                                        Guide Price
                                </p>
                            )}
                            <div className='col-12 flex justify-between items-center md-flex-col md-items-start lg-flex-row lg-items-center mb6 md-mb7'>
                                <div className='self-start'>
                                    {page.publicSellingStatus === 'Sold' && (
                                        <Tag
                                            className='pointer-events-none mb5'
                                            title='Sold property' />
                                    )}
                                    <h1 className='type-style-1 mb4'>
                                        {formatPrice(page?.priceGbp)}
                                    </h1>
                                    <p className='type-style-5'>
                                        Asking price
                                    </p>
                                </div>
                                {page.publicSellingStatus !== 'Sold' && rate && (
                                    <Tooltip
                                        className='currency-tooltip md-mt5 lg-mt0'
                                        currencyTooltip={true}
                                        icon={<CurrencyIcon />}
                                        id={page?.id}>
                                        <div>
                                            <p className='type-style-6 mb3'>
                                                {formatPrice(page?.priceGbp * rate, {currency: 'USD'})}
                                            </p>
                                            <p className='type-style-6 mb3'>
                                                {formatPrice(page?.priceGbp * fxRates.EUR * rate, {currency: 'EUR'})}
                                            </p>
                                            <p className='type-style-6 mb3'>
                                                {formatPrice(page?.priceGbp * fxRates.JPY * rate, {currency: 'JPY'})}
                                            </p>
                                            <p className='type-style-6 mb3'>
                                                {formatPrice(page?.priceGbp * fxRates.AED * rate, {currency: 'AED'})}
                                            </p>
                                            <p className='type-style-6 mb3'>
                                                {formatPrice(page?.priceGbp * fxRates.SAR * rate, {currency: 'SAR'})}
                                            </p>
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                            {page.publicSellingStatus !== 'Sold' && (
                                <div className='col-12 mt-auto'>
                                    <Button
                                        as='button'
                                        className='bg-black white type-style-5 items-center'
                                        look='fill'
                                        onClick={() => handleModal(
                                            <BookViewingModal
                                                hubspot={props.hubspot}
                                                {...page}/>)}
                                        renderIcon={<ArrowRightIcon />}>
                                        Book viewing
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='sm-hide'>
                        <button
                            aria-label='Stamp duty'
                            className='form-field-reset link-reset col-12 flex justify-between my5 cursor-pointer'
                            onClick={() => {
                                handleModal(<StampDutyModal {...page} />);
                            }}
                            type='button'>
                            <p className='type-style-5'>Stamp Duty</p>
                            <ArrowRightIcon />
                        </button>

                        {(page?.amenities?.length > 0) && (
                            <button
                                aria-label='Amenties'
                                className='form-field-reset link-reset col-12 flex justify-between my5 cursor-pointer'
                                onClick={() => {
                                    handleModal(<AmenitiesModal
                                        amenities={page?.amenities}
                                        description={page?.amenitiesDescription} />);
                                }}
                                type='button'>
                                <p className='type-style-5'>Amenities</p>
                                <ArrowRightIcon />
                            </button>
                        )}

                        <a
                            className='link-reset'
                            href={page.customPdf?.url ? page.customPdf.url : isProduction ? `https://www.russellsimpson.co.uk/generate-brochure/${props.data.uri}/${page.revisionId}` : `/brochure/${props.data?.uri}`}
                            rel='noreferrer'
                            target='_blank'>
                            <div className='col-12 flex justify-between my5'>
                                <p className='type-style-5'>Brochure</p>
                                <ArrowRightIcon />
                            </div>
                        </a>
                        <button
                            aria-label='Share'
                            className='form-field-reset link-reset col-12 flex justify-between cursor-pointer my5'
                            onClick={() => {
                                handleModal(<ShareModal />);
                            }}
                            type='button'>
                            <p className='type-style-5'>Share</p>
                            <ArrowRightIcon />
                        </button>
                    </div>

                    <div className='col-12 flex flex-wrap border'>
                        <div className='col-12 sm-col-6 md-col-3 flex flex-wrap sm-border-right'>
                            <div className='col-6 md-col-12 pt4 md-pt5 pl5 sm-pl6 property-header__cell border-bottom property-info-container border-right md-border-0 md-border-bottom'>
                                <p className='type-style-5 md-pt2 pb2'>Bedrooms</p>
                                <h3 className='type-style-3'>
                                    {formatRoomRange(page?.bedrooms, page?.bedroomsMax)}
                                </h3>
                            </div>
                            <div className='col-6 md-col-12 pt4 md-pt5 pl5 sm-pl6 property-header__cell border-bottom property-info-container '>
                                <p className='type-style-5 md-pt2 pb2'>Bathrooms</p>
                                <h3 className='type-style-3'>
                                    {formatRoomRange(page?.bathrooms, page?.bathroomsMax)}
                                </h3>
                            </div>
                        </div>
                        <div className='col-12 sm-col-6 md-col-3 flex flex-wrap pt4 md-pt5 pl5 sm-pl6 md-border-right border-bottom property-header__double-cell'>
                            <div>
                                <p className='type-style-5 md-pt2 pb2'>Internal</p>
                                {(page?.internalAreaFeet && page?.internalAreaMetres) &&
                                    <div className='flex md-block'>
                                        {(page?.internalAreaFeet) &&
                                            <div className='md-pt5 md-pb2 pr8 md-pr0 flex items-baseline'>
                                                <h3 className='type-style-2 md-type-style-1'>
                                                    {formatNumber(page?.internalAreaFeet)}
                                                </h3>
                                                <p className='type-style-5 ml2'>sq ft</p>
                                            </div>
                                        }
                                        {(page?.internalAreaMetres) &&
                                            <div className='md-pt5 flex items-baseline'>
                                                <h3 className='type-style-2 md-type-style-1'>
                                                    {formatNumber(page?.internalAreaMetres)}
                                                </h3>
                                                <p className='type-style-5 ml2'>sqm</p>
                                            </div>
                                        }
                                    </div>
                                }
                                {!(page?.internalAreaFeet && page?.internalAreaMetres) &&
                                    <p className='type-style-3'>–</p>
                                }
                            </div>
                        </div>
                        <div className='col-12 md-col-3 flex flex-wrap md-border-right'>
                            <div className='col-6 md-col-12 pt4 md-pt5 pl5 sm-pl6 property-header__cell border-bottom property-info-container border-right md-border-0 md-border-bottom'>
                                <p className='type-style-5 md-pt2 pb2'>Outdoor</p>
                                {(page?.outdoorAreaFeet) &&
                                    <div className='flex items-baseline'>
                                        <h3 className='type-style-3'>
                                            {formatNumber(page.outdoorAreaFeet)}
                                        </h3>
                                        <p className='type-style-5 ml2'>sq ft</p>
                                    </div>
                                }
                                {!(page?.outdoorAreaFeet) &&
                                    <p className='type-style-3'>–</p>
                                }
                            </div>
                            <div className='col-6 md-col-12 pt4 md-pt5 pl5 sm-pl6 property-header__cell border-bottom property-info-container '>
                                {(page?.epcRating) &&
                                    <>
                                        <p className='type-style-5 md-pt2 pb2'>EPC</p>
                                        <h3 className='type-style-3'>{page?.epcRating}</h3>
                                    </>
                                }
                                {!(page?.epcRating) &&
                                    <p className='type-style-3'>–</p>
                                }
                            </div>
                        </div>
                        <div className='col-12 md-col-3 flex flex-wrap'>
                            <div className='col-12 sm-col-6 md-col-12 pt4 md-pt5 pl5 sm-pl6 property-header__cell border-bottom property-info-container sm-border-right md-border-0 md-border-bottom'>
                                <p className='type-style-5 md-pt2 pb2'>Tenure</p>
                                <div className='flex items-center'>
                                    <h3 className='type-style-3 inline'>{page?.tenure}</h3>
                                    {(page?.tenure === 'Leasehold' || page?.tenure === 'Share of freehold') && (
                                        <Tooltip
                                            icon='i'
                                            id={page?.id}>
                                            <div>
                                                {(page.tenureNotes) ?
                                                    <div
                                                        className='rich-text tooltip type-style-6'
                                                        dangerouslySetInnerHTML={{ __html: page.tenureNotes }} />
                                                    :(
                                                        <>
                                                            {(page.leaseLength) ? (
                                                                <div className='mb3'>
                                                                    <p className='type-style-6'>
                                                                        Length of lease left:
                                                                    </p>
                                                                    <p className='type-style-6'>
                                                                        {page.leaseLength} years
                                                                    </p>
                                                                </div>
                                                            ): null}
                                                            {(page.groundRent) ? (
                                                                <div className='mb3'>
                                                                    <p className='type-style-6'>
                                                                        Ground rent:
                                                                    </p>
                                                                    <p className='type-style-6'>
                                                                        {formatPrice(page.leaseLength, {useGrouping: false})} p/y
                                                                    </p>
                                                                </div>
                                                            ) : null}
                                                            {(page.serviceCharge) ? (
                                                                <div className='mb3'>
                                                                    <p className='type-style-6'>
                                                                        Service charge:
                                                                    </p>
                                                                    <p className='type-style-6'>
                                                                        {formatPrice(page.serviceCharge, {useGrouping: false})} p/y
                                                                    </p>
                                                                </div>
                                                            ) : null}
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            <div className='col-12 sm-col-6 md-col-12 pt4 md-pt5 pl5 sm-pl6 property-header__cell border-bottom property-info-container '>
                                <p className='type-style-5 md-pt2 pb2'>Council Tax</p>
                                <h3 className='type-style-3'>
                                    {page?.localAuthority ? page.localAuthority : ''}
                                    {page?.localAuthority && page?.councilTaxBand ? ', ' : ''}
                                    {page?.councilTaxBand ? page.councilTaxBand : ''}
                                    {!page?.councilTaxBand && !page.localAuthority ? '–' : ''}
                                </h3>
                            </div>
                        </div>
                        <div className='col-6 pt4 md-pt5 pl3 sm-pl6 pr3 sm-pr6 md-pr7 property-header__media-cell relative border-right'>
                            <p className='type-style-5'>Map</p>
                            <div className='hide md-show absolute z2 right right-0 mr5'>
                                <Button
                                    as='button'
                                    className = 'bg-black white p3 md-p5'
                                    look='button'
                                    onClick={() => {
                                        handleModal(<MapModal {...page} />);
                                    }}
                                    renderIcon={<AddIcon />} />
                            </div>
                            <div className='property-info-preview relative md-mt5'>
                                <div className='md-hide'>
                                    <Button
                                        as='button'
                                        className='bg-black white p3 md-p5 absolute-center z2'
                                        look='button'
                                        onClick={() => {
                                            handleModal(<MapModal {...page} />);
                                        }}
                                        renderIcon={<AddIcon />} />
                                </div>
                                <div
                                    className='width-100 height-100 bg-black absolute top-0 z1 cursor-pointer'
                                    onClick={() => {
                                        handleModal(<MapModal {...page} />);
                                    }}
                                    style={{opacity: '0.2'}}></div>
                                <img
                                    className='width-100'
                                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${page.mapLatitude},${page.mapLongitude}&zoom=14&size=900x200&format=png&style=feature:all%7Celement:geometry%7Cvisibility:simplified%7Csaturation:-100&style=feature:road%7Cvisibility:simplified%7Ccolor:0x000000&style=feature:poi%7Cvisibility:off&style=feature:all%7Celement:labels.text.stroke%7Cvisibility:off&style=feature:all%7Celement:labels.text.fill%7Cvisibility:off&style=feature:transit%7Cvisibility:off&key=${MAP_KEY}`}/>

                            </div>
                        </div>
                        <div className='col-6 pt4 md-pt5 pl3 sm-pl6 pr3 sm-pr6 md-pr7 property-header__media-cell relative'>
                            <p className='type-style-5'>Floorplan</p>
                            <div className='hide md-show absolute z2 right right-0 mr5'>
                                <Button
                                    as='button'
                                    className='bg-black white p3 md-p5'
                                    look='button'
                                    onClick={() => {
                                        handleModal(<FloorplanModal images={floorplanImages} />);
                                    }}
                                    renderIcon={<AddIcon />} />
                            </div>
                            <div className='property-info-preview relative md-mt5 filter-grayscale'>
                                <div className='md-hide'>
                                    <Button
                                        as='button'
                                        className='bg-black white p3 md-p5 absolute-center z2'
                                        look='button'
                                        onClick={() => {
                                            handleModal(<FloorplanModal images={floorplanImages} />);
                                        }}
                                        renderIcon={<AddIcon />} />
                                </div>
                                <div
                                    className='width-100 height-100 bg-black absolute top-0 z1 cursor-pointer'
                                    onClick={() => {
                                        handleModal(<FloorplanModal images={floorplanImages} />);
                                    }}
                                    style={{opacity: '0.2'}}/>
                                <Image {...floorplanImages?.[0]} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* modules */}
            <Stream
                borderTop={false}
                currentPage={page}
                keyLinks={props?.data?.keyLinks}
                modules={page?.modules}
                namespace='content-page' />
        </main>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PropertyPage;
